/* ==================================================================
   The Golden Quest · API layer
   GET /golden-quest (the page payload, with the streaks scored server-side
   from the user's transactions) and POST /golden-quest/action (one user
   action, picked by `action`) — gosats-apis, goSatsCampaignRoot. Maps
   `resp.goldenQuest` onto the model the page renders; the static copy in
   campaign.js is the fallback for anything the backend doesn't send.
   ================================================================== */
import * as APIHelper from '../Helper/apiFetch';
import { GQ_MOCK_RESPONSES, GQ_MOCK_SCENARIO } from './mockResponse';
import {
  LAUNCH, END, DRAW_ON, PRE_REG_GEMS, REFERRAL_CODE, GRAND_PRIZE_COUNT, PENDING_REWARD,
  STREAKS, STREAK_NAME, GEM_SOURCE_LABEL, GRAND_TOP, GRAND_ALL, PRIZE_TIERS, TERMS, FAQS,
  shareMessageFor, phaseFromDates,
} from './campaign';

/* Outside production builds, `?scenario=<preset>` on the URL serves a preset
   from mockResponse.js instead of calling the backend, so every flow previews
   in a plain browser without a session token. Without it the page talks to
   the API. */
const MOCK_SCENARIO = new URLSearchParams(window.location.search).get('scenario');
const USE_MOCK = process.env.NODE_ENV !== 'production' && MOCK_SCENARIO !== null;
const MOCK_LATENCY_MS = 400;

const STATUS_PHASE = { PRE_LAUNCH: 'pre', LIVE: 'live', ENDED: 'ended' };

const mockDelay = (value, ms = MOCK_LATENCY_MS) => new Promise((resolve) => setTimeout(() => resolve(value), ms));

function mockResponse() {
  return GQ_MOCK_RESPONSES[MOCK_SCENARIO] || GQ_MOCK_RESPONSES[GQ_MOCK_SCENARIO];
}

const toDate = (v, fallback) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d : fallback;
};

/* the static streak definitions (icons, sizes) overlaid with the API's copy
   and progress; the open streaks are listed first, the locked ones after
   (each group in the design's order) */
function mapStreaks(apiStreaks) {
  const byKey = {};
  (apiStreaks || []).forEach((s) => { if (s && s.key) byKey[s.key] = s; });
  const streaks = STREAKS.map((s) => {
    const api = byKey[s.key] || {};
    return {
      ...s,
      /* the backend decides when a streak opens: `enabled: false` → the card
         is locked (lock chip, "Unlocks" CTA), whatever the campaign phase */
      locked: api.enabled === false,
      title: api.title || s.title,
      desc: api.text || s.desc,
      labels: Array.isArray(api.labels) && api.labels.length === 5 ? api.labels : s.labels,
      cta: (api.cta && api.cta.text) || s.cta,
      action: (api.cta && api.cta.action) || null,
      progress: Number.isFinite(api.progress) ? Math.max(0, Math.min(api.progress, 4)) : 0,
      completed: api.completed === true,
      extraGems: Number.isFinite(api.extraGems) ? api.extraGems : 0,
    };
  });
  return [...streaks.filter((s) => !s.locked), ...streaks.filter((s) => s.locked)];
}

/* what an opened Gem holds. The backend's reward selection is still TODO and
   sends `reward: null`; until it lands, an opened Gem shows as pending rather
   than as unopened (its open is already recorded, so it can't be opened again). */
export function mapReward(r) {
  if (!r || !r.name) return PENDING_REWARD;
  return { name: r.name, detail: r.detail || '', kind: r.kind || '' };
}

/* a Gem: where it came from, when, and — once opened — what it held
   (`openedAt` + `reward` in the API; `opened: { at, reward } | null` here) */
export function mapGem(g, i = 0) {
  const opened = g.openedAt
    ? { at: toDate(g.openedAt, new Date()), reward: mapReward(g.reward) }
    : null;
  return {
    id: String(g.id ?? `gem-${i}`),
    source: g.source || '',
    sourceLabel: GEM_SOURCE_LABEL[g.source] || g.source || 'Gem',
    earnedAt: toDate(g.earnedAt, LAUNCH),
    opened,
  };
}

/* a Gold Chest: the streak that earned it, and its draw result once drawn */
export function mapChest(c, i = 0) {
  return {
    id: String(c.id ?? `chest-${i}`),
    streak: c.streak || '',
    streakName: STREAK_NAME[c.streak] || c.streak || 'Streak',
    earnedAt: toDate(c.earnedAt, new Date()),
    celebrated: c.celebrated !== false,
    result: c.result && c.result.kind
      ? { kind: c.result.kind, prize: c.result.prize || null, rank: c.result.rank || null }
      : null,
  };
}

function mapGrandPrizes(gp) {
  const top = gp && Array.isArray(gp.top) && gp.top.length
    ? gp.top.map((p) => ({ art: p.art, name: p.name, sub: p.sub, win: p.rank, badge: p.badge }))
    : GRAND_TOP;
  const all = gp && Array.isArray(gp.all) && gp.all.length ? gp.all : GRAND_ALL;
  const tiers = gp && Array.isArray(gp.tiers) && gp.tiers.length ? gp.tiers : PRIZE_TIERS;
  const sum = (list) => list.reduce((n, p) => n + (p.winners || 0), 0);
  return {
    total: (gp && gp.total) || GRAND_PRIZE_COUNT,
    drawOn: toDate(gp && gp.drawOn, DRAW_ON),
    top,
    all,
    tiers,
    tierWinners: (gp && gp.tierWinners) || sum(tiers),
    totalPrizes: (gp && gp.totalPrizes) || ((gp && gp.total) || GRAND_PRIZE_COUNT) + ((gp && gp.tierWinners) || sum(tiers)),
  };
}

/* grand draw results (ENDED); null until the draw is published. `you.rank`
   points at the user's own row in `winners`, which renders as "You". */
function mapDraw(d) {
  if (!d || !Array.isArray(d.winners)) return null;
  const you = d.you || {};
  const won = you.won === true;
  const rank = Number.isFinite(you.rank) ? you.rank : null;
  return {
    drawnAt: toDate(d.drawnAt, DRAW_ON),
    you: {
      won,
      rank,
      prize: you.prize || null,
      title: you.title || (won ? `You won ${you.prize ? 'a ' + you.prize : 'a grand prize'}` : 'No grand prize this time'),
      text: you.text || (won ? 'Shipping to your registered address.' : 'Your Gems and their rewards are still yours.'),
      kicker: you.kicker || (won ? 'Delivery details on their way by SMS' : 'See you at the next festival'),
    },
    winners: d.winners.map((w, i) => {
      const isYou = won && rank === i + 1;
      return { name: isYou ? 'You' : w.name, city: w.city || '', prize: w.prize, isYou };
    }),
  };
}

/* maps resp.goldenQuest → page model, or null when the campaign is off */
export function mapGoldenQuest(resp) {
  const data = resp && (resp.data ?? resp);
  const gq = data && data.goldenQuest;
  if (!gq || gq.enabled === false) return null;

  const launch = toDate(gq.campaignStart, LAUNCH);
  const end = toDate(gq.campaignEnd, END);
  const user = gq.user || {};
  const wallet = gq.wallet || {};
  const registered = user.preRegistered === true;
  const preRegGems = Number.isFinite(user.preRegGems) ? user.preRegGems : PRE_REG_GEMS;
  const referralCode = user.referralCode || REFERRAL_CODE;
  const gems = Array.isArray(wallet.gems) ? wallet.gems.map(mapGem) : [];
  const chests = Array.isArray(wallet.chests) ? wallet.chests.map(mapChest) : [];
  /* the backend's terms, as sections (see campaign.js TERMS) or the older flat
     `items` list; the static copy only when it sends neither */
  const hasList = (v) => Array.isArray(v) && v.length > 0;
  const terms = gq.terms && (hasList(gq.terms.sections) || hasList(gq.terms.items)) ? gq.terms : TERMS;

  return {
    phase: STATUS_PHASE[gq.status] || phaseFromDates(launch, end),
    launch,
    end,
    registered,
    preRegGems,
    notify: user.reminderSet === true,
    storyEnabled: !(gq.story && gq.story.enabled === false),
    storySeen: user.storySeen === true,
    liveIntroSeen: user.liveIntroSeen === true,
    gems,
    chests,
    referralCode,
    shareMessage: (gq.share && gq.share.message) || shareMessageFor(referralCode),
    streaks: mapStreaks(gq.streaks),
    grandPrizes: mapGrandPrizes(gq.grandPrizes),
    draw: mapDraw(gq.draw),
    terms: { summary: terms.summary || '', sections: terms.sections || null, items: terms.items || null },
    faqs: Array.isArray(gq.faqs) && gq.faqs.length ? gq.faqs : FAQS,
  };
}

/* Resolves to the page model, or null when the campaign is disabled (nothing
   to show). A genuine network / auth failure throws so the page can retry. */
export async function fetchGoldenQuest() {
  if (USE_MOCK) return mapGoldenQuest(await mockDelay(mockResponse()));

  const res = await APIHelper.goldenQuest();
  const data = res && (res.data ?? res);
  /* `{}` is apiFetch's answer while there is no session token yet: a failure,
     not a disabled campaign, so the session listener can re-run the load */
  if (!res || res.error || !data || !data.goldenQuest) {
    throw new Error((res && res.message) || 'Golden Quest request failed');
  }
  return mapGoldenQuest(res);
}

/* ---- write calls ----
   All go through POST /golden-quest/action, picked by `action`. The backend
   answers `{ error: false, ... }` on success and `{ error: true, message }`
   when it declines (pre-registration closed, Gem locked or unknown, chest
   unknown); apiFetch answers `{}` without a session token. Anything but a
   success throws; a decline carries the backend's message with `refused` set
   so the page can show it as is. While mocking they resolve with the
   backend's shapes (and the prototype's timing) so the flows can depend on
   them. */

async function sendAction(body) {
  const res = await APIHelper.goldenQuestAction(body);
  if (res && res.error === false) return res;
  const err = new Error((res && res.message) || `Golden Quest ${body.action} failed`);
  err.refused = !!(res && res.error === true && res.message);
  throw err;
}

/* the user has watched the story; the next open skips it (`user.storySeen`) */
export async function markStorySeen() {
  if (USE_MOCK) return { error: false, storySeen: true };
  return sendAction({ action: 'STORY_SEEN' });
}

/* the Gems pre-registration reserves, as the backend reports them */
function mockPreRegGems() {
  return Array.from({ length: PRE_REG_GEMS }, (_, i) => ({
    id: `prereg-${i + 1}`, source: 'PRE_REG', earnedAt: LAUNCH.getTime(), openedAt: null, reward: null,
  }));
}

/* Claims the free pre-registration Gems. `notify` is the "Remind me when it
   starts" toggle: it sets the launch reminder, and the backend tracks the
   claim (with the toggle) only when it is sent. Resolves to
   { preRegistered, reminderSet, gems[] } — the Gems the backend reserved,
   with the ids OPEN_GEM accepts from launch. A repeat reports the stored
   registration (`alreadyRegistered`). */
export async function preRegister({ notify }) {
  if (USE_MOCK) {
    /* ~0.9s like the prototype's "Adding your Gems…" beat */
    return mockDelay({ error: false, preRegistered: true, reminderSet: notify === true, gems: mockPreRegGems() }, 900);
  }
  return sendAction({ action: 'PRE_REGISTER', notify: notify === true });
}

/* turns the launch reminder on / off (`user.reminderSet`) */
export async function setReminder(on) {
  if (USE_MOCK) return { error: false, reminderSet: on === true };
  return sendAction({ action: 'REMINDER', on: on === true });
}

/* the user has seen the "festival is open" interstitial (`user.liveIntroSeen`) */
export async function markLiveIntroSeen() {
  if (USE_MOCK) return { error: false, liveIntroSeen: true };
  return sendAction({ action: 'LIVE_INTRO_SEEN' });
}

/* the user has seen the celebration for a chest (`wallet.chests[].celebrated`) */
export async function markChestCelebrated(chestId) {
  if (USE_MOCK) return { error: false, chestId, celebrated: true };
  return sendAction({ action: 'CHEST_CELEBRATED', chestId: String(chestId) });
}

/* what a mock Gem can hold */
const MOCK_REWARDS = [
  { name: '₹25 in Gold', detail: 'Added to your gold vault', kind: 'GOLD' },
  { name: '100 Sats', detail: 'Added to your Bitcoin balance', kind: 'SATS' },
  { name: '₹100 Amazon voucher', detail: 'Saved under Vouchers', kind: 'VOUCHER' },
  { name: '₹50 in Gold', detail: 'Added to your gold vault', kind: 'GOLD' },
  { name: '₹10 cashback', detail: 'Credited to your card', kind: 'CASHBACK' },
  { name: '250 Sats', detail: 'Added to your Bitcoin balance', kind: 'SATS' },
  { name: '₹200 Flipkart voucher', detail: 'Saved under Vouchers', kind: 'VOUCHER' },
  { name: '2× SuperCoins for a day', detail: 'Active on your next card spend', kind: 'BOOST' },
];

/* Opens a Gem. Resolves to { reward: { name, detail, kind }, openedAt: Date,
   alreadyOpened } — the reward is decided server-side; the page only reveals
   it (pending, via mapReward, until the backend's reward selection lands). A
   Gem that is already open reports what it holds instead of failing. */
export async function openGem(gemId) {
  let res;
  if (USE_MOCK) {
    /* deterministic per Gem so a preview is repeatable; ~0.75s like the prototype's shake */
    const n = String(gemId).split('').reduce((h, ch) => h + ch.charCodeAt(0), 0);
    const reward = MOCK_REWARDS[(n * 7 + 3) % MOCK_REWARDS.length];
    res = await mockDelay({ error: false, gemId, openedAt: Date.now(), reward }, 750);
  } else {
    res = await sendAction({ action: 'OPEN_GEM', gemId: String(gemId) });
  }
  return { reward: mapReward(res.reward), openedAt: toDate(res.openedAt, new Date()), alreadyOpened: res.alreadyOpened === true };
}
